import React, { useState, useEffect, useRef } from 'react';
import ImageArranger from '.././components/dtf/ImageArranger';
import ProceedToCheckout from '.././components/dtf/ptc';
import UploadButton from '../components/dtf/UploadButton';
import ClockIcon from '@mui/icons-material/AccessTime';
import { v4 as uuidv4 } from 'uuid';
import { roundSize } from '../utils/money';
import { DPI } from '../config/dtfConfig';
import { auth, db, functions } from '../firebase';
import { getStorage, ref, listAll, getDownloadURL } from "firebase/storage";
import RecentlyUploaded from '../components/dtf/RecentlyUploaded';
import { Button, Paper, Typography, Box } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { onAuthStateChanged, sendEmailVerification } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

const ENFORCE_EMAIL_VERIFICATION_IN_PORTAL = false;
const RESEND_COOLDOWN_MS = 60000;
let verificationSyncPermissionWarned = false;

// --- drag & drop helpers ---
function isImageFile(file) {
  return file && file.type && file.type.startsWith('image/');
}
function loadImageMetaFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const trimTransparentBorder = (imgEl, alphaThreshold = 10) => {
        try {
          const w = imgEl.naturalWidth || imgEl.width;
          const h = imgEl.naturalHeight || imgEl.height;
          if (!w || !h) return { changed: false };
          const c = document.createElement('canvas'); c.width = w; c.height = h;
          const x = c.getContext('2d'); x.drawImage(imgEl, 0, 0);
          const { data } = x.getImageData(0, 0, w, h);
          let top = 0, left = 0, right = w - 1, bottom = h - 1;
          const isOpaqueAt = (px) => data[px + 3] > alphaThreshold;
          for (; top <= bottom; top++) { let f = false; const row = top * w * 4; for (let col = 0; col < w; col++) { if (isOpaqueAt(row + col * 4)) { f = true; break; } } if (f) break; }
          for (; bottom >= top; bottom--) { let f = false; const row = bottom * w * 4; for (let col = 0; col < w; col++) { if (isOpaqueAt(row + col * 4)) { f = true; break; } } if (f) break; }
          for (; left <= right; left++) { let f = false; for (let row = top; row <= bottom; row++) { const px = row * w * 4 + left * 4; if (isOpaqueAt(px)) { f = true; break; } } if (f) break; }
          for (; right >= left; right--) { let f = false; for (let row = top; row <= bottom; row++) { const px = row * w * 4 + right * 4; if (isOpaqueAt(px)) { f = true; break; } } if (f) break; }
          const cropW = Math.max(0, right - left + 1); const cropH = Math.max(0, bottom - top + 1);
          if (cropW <= 0 || cropH <= 0 || (cropW === w && cropH === h)) return { changed: false };
          const out = document.createElement('canvas'); out.width = cropW; out.height = cropH;
          out.getContext('2d').drawImage(c, left, top, cropW, cropH, 0, 0, cropW, cropH);
          return { changed: true, canvas: out };
        } catch (e) { return { changed: false }; }
      };

      let srcOut = url; let pxW = img.width; let pxH = img.height;
      const t = trimTransparentBorder(img, 10);
      if (t.changed && t.canvas) {
        try { srcOut = t.canvas.toDataURL('image/png'); pxW = t.canvas.width; pxH = t.canvas.height; } catch (_) { }
      }
      const widthIn = roundSize(pxW / DPI);
      const heightIn = roundSize(pxH / DPI);
      resolve({
        name: file.name,
        widthIn,
        heightIn,
        quantity: 1,
        src: srcOut,
        file,
        lockAspectRatio: true,
      });
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

// Native IndexedDB helpers for session persistence
function getDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('dtfSessionDb');
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('sessions')) {
        db.createObjectStore('sessions');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function saveSessionData(sid, partialData) {
  const db = await getDb();
  const tx = db.transaction('sessions', 'readwrite');
  const store = tx.objectStore('sessions');
  const existing = (await new Promise((res, rej) => {
    const r = store.get(sid);
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  })) || {};
  const merged = { ...existing, ...partialData };
  await new Promise((res, rej) => {
    const w = store.put(merged, sid);
    w.onsuccess = () => res();
    w.onerror = () => rej(w.error);
  });
}

async function loadSessionData(sid) {
  const db = await getDb();
  const store = db.transaction('sessions', 'readonly').objectStore('sessions');
  return (await new Promise((res, rej) => {
    const r = store.get(sid);
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  })) || {};
}

const resolveVerificationRedirectUrl = () => {
  const envUrl =
    process.env.REACT_APP_EMAIL_VERIFICATION_REDIRECT_URL ||
    process.env.REACT_APP_APP_BASE_URL ||
    process.env.REACT_APP_CLIENT_BASE_URL;
  if (envUrl) return envUrl;
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/verify-email`;
  }
  return "https://sodtf.com/verify-email";
};



// Replace with your actual video file path
const SHEET_TUTORIAL_VIDEO = "/videos/tutorial.mp4";

function VideoWithPlayOverlay() {
  const videoRef = React.useRef(null);
  const [playing, setPlaying] = React.useState(false);

  const handlePlay = () => {
    setPlaying(true);
    videoRef.current && videoRef.current.play();
  };

  const handlePause = () => setPlaying(false);

  return (
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
      <video
        ref={videoRef}
        width="100%"
        height="100%"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          borderRadius: 4,
        }}
        src={SHEET_TUTORIAL_VIDEO}
        onPause={handlePause}
        onEnded={handlePause}
        controls={playing}
        poster="/videos/sheet-builder-tutorial-thumb.jpg" // Optionally add a thumbnail image
      />
      {!playing && (
        <div
          onClick={handlePlay}
          style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(255,255,255,1)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            cursor: 'pointer',
            borderRadius: 4,
            transition: 'background 0.2s'
          }}
        >
          <span style={{ fontSize: '2.8rem', color: '#94C940', borderRadius: '50%', backgroundColor: 'transparent', padding: '0.4em 0.55em' }}>
            ▶
          </span>
        </div>
      )}
    </div>
  );
}

function App() {
  const [images, setImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [sheetDataUrls, setSheetDataUrls] = useState([]);
  const [sheetGroups, setSheetGroups] = useState([]);
  const [orderDetails, setOrderDetails] = useState([]);
  const [totalImages, setTotalImages] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [squareInches, setSquareInches] = useState(0);
  const [pictures, setPictures] = useState([]);
  const [previewMode, setPreviewMode] = useState('full');
  const [recentImages, setRecentImages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showRecentDialog, setShowRecentDialog] = useState(false);
  const arrangerRef = useRef();
  const proceedRef = useRef();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [sessionId, setSessionId] = useState(null);

  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const [fatalErrorOverlay, setFatalErrorOverlay] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState('');
  const [resendingVerification, setResendingVerification] = useState(false);
  const [lastVerificationSentAt, setLastVerificationSentAt] = useState(() => {
    const stored = typeof window !== "undefined"
      ? window.localStorage.getItem("sodtfVerificationLastSentAt")
      : null;
    const num = stored ? Number(stored) : null;
    return Number.isFinite(num) ? num : null;
  });
  const [cooldownMsLeft, setCooldownMsLeft] = useState(() =>
    lastVerificationSentAt ? Math.max(0, RESEND_COOLDOWN_MS - (Date.now() - lastVerificationSentAt)) : 0
  );
  const [verificationChecked, setVerificationChecked] = useState(false);
  const verificationRedirectUrl = resolveVerificationRedirectUrl();

  useEffect(() => {
    const computeRemaining = () => {
      if (!lastVerificationSentAt) return 0;
      return Math.max(0, RESEND_COOLDOWN_MS - (Date.now() - lastVerificationSentAt));
    };
    setCooldownMsLeft(computeRemaining());
    if (!lastVerificationSentAt) return;
    const id = setInterval(() => {
      const remaining = computeRemaining();
      setCooldownMsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(id);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [lastVerificationSentAt]);

  const evaluateVerificationStatus = async (user) => {
    if (!user || !ENFORCE_EMAIL_VERIFICATION_IN_PORTAL) {
      setNeedsVerification(false);
      setVerificationLoading(false);
      setVerificationChecked(true);
      return false;
    }
    setVerificationLoading(true);
    try {
      try {
        await user.reload();
      } catch (reloadErr) {
        console.warn("CustomerPortal: failed to reload user for verification check:", reloadErr);
      }
      let needs = !user.emailVerified;
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) {
          const data = snap.data() || {};
          if (data.emailVerified === true) needs = false;
          if (data.emailVerified === false) needs = true;
          if (data.requireEmailReverify === true) needs = true;
          if (user.emailVerified && data.emailVerified !== true) {
            await updateDoc(doc(db, "users", user.uid), {
              emailVerified: true,
              requireEmailReverify: false,
            });
          }
        } else if (user.emailVerified) {
          await updateDoc(doc(db, "users", user.uid), {
            emailVerified: true,
            requireEmailReverify: false,
          });
        }
      } catch (docErr) {
        if (docErr?.code === "permission-denied") {
          // Read/write may be blocked by security rules; keep using auth state as source of truth.
          if (!verificationSyncPermissionWarned) {
            console.info("CustomerPortal: verification doc sync skipped (permission-denied)");
            verificationSyncPermissionWarned = true;
          }
        } else {
          console.warn("CustomerPortal: verification doc sync failed:", docErr);
        }
      }
      setNeedsVerification(needs);
      if (!needs) setVerificationStatus("");
      setVerificationChecked(true);
      return needs;
    } finally {
      setVerificationLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setVerificationChecked(false);
      if (!user) {
        setNeedsVerification(false);
        setVerificationLoading(false);
        return;
      }
      await evaluateVerificationStatus(user);
    });
    return () => unsubscribe();
  }, []);

  const handleResendVerification = async () => {
    if (!currentUser) return;
    const remaining = cooldownMsLeft;
    if (remaining > 0) {
      const seconds = Math.ceil(remaining / 1000);
      setVerificationStatus(`Please wait ${seconds}s before requesting another verification email.`);
      return;
    }
    setResendingVerification(true);
    setVerificationStatus("");
    try {
      const callable = httpsCallable(functions, "sendVerificationEmailCustom");
      await callable({ redirectUrl: verificationRedirectUrl });
      setVerificationStatus(`Verification email sent to ${currentUser.email}.`);
      const now = Date.now();
      setLastVerificationSentAt(now);
      setCooldownMsLeft(RESEND_COOLDOWN_MS);
      if (typeof window !== "undefined") {
        window.localStorage.setItem("sodtfVerificationLastSentAt", String(now));
      }
    } catch (err) {
      console.error("CustomerPortal: custom verification send failed:", err);
      try {
        await sendEmailVerification(currentUser, {
          url: verificationRedirectUrl,
          handleCodeInApp: true,
        });
        setVerificationStatus(`Verification email sent to ${currentUser.email}.`);
        const now = Date.now();
        setLastVerificationSentAt(now);
        setCooldownMsLeft(RESEND_COOLDOWN_MS);
        if (typeof window !== "undefined") {
          window.localStorage.setItem("sodtfVerificationLastSentAt", String(now));
        }
      } catch (fallbackErr) {
        console.error("CustomerPortal: fallback verification send failed:", fallbackErr);
        setVerificationStatus(
          fallbackErr?.message || "Unable to send verification email right now."
        );
      }
    } finally {
      setResendingVerification(false);
    }
  };

  const handleCheckVerified = async () => {
    if (!currentUser) return;
    setVerificationLoading(true);
    setVerificationStatus("");
    try {
      try {
        await currentUser.reload();
      } catch (reloadErr) {
        console.warn("CustomerPortal: reload before verification check failed:", reloadErr);
      }

      if (currentUser.emailVerified) {
        try {
          const markVerified = httpsCallable(functions, "markEmailVerified");
          await markVerified();
        } catch (fnErr) {
          console.warn("CustomerPortal: markEmailVerified callable failed:", fnErr);
          try {
            await updateDoc(doc(db, "users", currentUser.uid), {
              emailVerified: true,
              requireEmailReverify: false,
            });
          } catch (fallbackErr) {
            console.warn("CustomerPortal: fallback verification sync failed:", fallbackErr);
          }
        }
        setNeedsVerification(false);
        setVerificationStatus("Thanks! Your email is verified.");
        return;
      }

      const stillNeeds = await evaluateVerificationStatus(currentUser);
      if (stillNeeds) {
        setVerificationStatus(
          "We haven't detected verification yet. Please click the email link, then hit “I verified” again."
        );
      } else {
        setVerificationStatus("Thanks! Your email is verified.");
      }
    } finally {
      setVerificationLoading(false);
    }
  };

  const loadRecentImages = async (uid) => {
    const storage = getStorage();
    const userRef = ref(storage, `pictures/${uid}`);
    try {
      const result = await listAll(userRef);
      const entries = await Promise.all(result.items.map(async (item) => {
        const url = await getDownloadURL(item);
        return { url, name: item.name };
      }));
      setRecentImages(entries);
    } catch (error) {
      console.error("CPO-001 Failed to load recent images:", error);
      setFatalErrorOverlay({
        code: 'CPO-001',
        title: 'Could Not Load Recent Uploads',
        message: error?.message || 'The recent images list is unavailable right now.',
      });
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let sid = params.get('sessionId');
    if (!sid) {
      sid = uuidv4();
      params.set('sessionId', sid);
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState(null, '', newUrl);
    }
    setSessionId(sid);

    (async () => {
      try {
        const data = await loadSessionData(sid);
        if (data?.images) setImages(data.images);
      } catch (err) {
        console.error('CPO-002 Failed to load saved session:', err);
        setFatalErrorOverlay({
          code: 'CPO-002',
          title: 'Session Restore Failed',
          message: err?.message || 'We could not restore your previous arrangement.',
        });
      }
    })();
  }, []);

  const handleDropFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(isImageFile);
    if (!files.length) return;
    try {
      const newImages = await Promise.all(files.map(loadImageMetaFromFile));
      setImages(prev => {
        const updated = [...prev, ...newImages];
        const serializableImages = updated.map(img => ({
          name: img.name || img.fileName || img.file?.name,
          widthIn: img.widthIn,
          heightIn: img.heightIn,
          quantity: img.quantity || 1,
          src: img.url || img.previewUrl || img.src,
          lockAspectRatio: true,
        }));
        if (sessionId) saveSessionData(sessionId, { images: serializableImages });
        return updated;
      });
    } catch (e) {
      console.error('CPO-003 Drop upload failed', e);
      setFatalErrorOverlay({
        code: 'CPO-003',
        title: 'Upload Failed',
        message: e?.message || 'We were unable to process one of the uploaded files.',
      });
    }
  };

  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    setIsDragging(true);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      setIsDragging(false);
      dragCounter.current = 0;
    }
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;
    if (e.dataTransfer && e.dataTransfer.files) {
      handleDropFiles(e.dataTransfer.files);
    }
  };

  const dropzoneProps = {
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
    style: { position: 'relative' },
  };

  const handleProceedToCheckout = async () => {
    let success = false;
    if (!proceedRef.current || typeof proceedRef.current.proceed !== 'function') {
      console.error('CPO-004 Missing proceedRef');
      setFatalErrorOverlay({
        code: 'CPO-004',
        title: 'Checkout Launch Failed',
        message: 'Unable to initialise checkout. Please refresh or contact support.',
      });
      return;
    }

    for (let attempt = 0; attempt < 5; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, attempt === 0 ? 0 : 50));
      success = await proceedRef.current.proceed();
      if (success) break;
    }

    if (!success) {
      console.error('CPO-004 Checkout proceed failed after retries');
      setFatalErrorOverlay({
        code: 'CPO-004',
        title: 'Checkout Launch Failed',
        message: 'We could not move your order to checkout. Please retry or contact support.',
      });
    }
  };

  return (
    <>
      {fatalErrorOverlay && (
        <Box sx={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          zIndex: 1600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}>
          <Paper elevation={12} sx={{ maxWidth: 480, width: '100%', p: 3, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {fatalErrorOverlay.title || 'Portal Error'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Code {fatalErrorOverlay.code}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {fatalErrorOverlay.message || 'Something went wrong while preparing your order.'}
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setFatalErrorOverlay(null)}
            >
              Dismiss
            </Button>
          </Paper>
        </Box>
      )}
      {currentUser && verificationChecked && needsVerification && (
        <Box sx={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(15,23,42,0.65)',
          zIndex: 1500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}>
          <Paper elevation={10} sx={{ maxWidth: 500, width: '100%', p: 3, borderRadius: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Verify your email
            </Typography>
            <Typography sx={{ color: 'text.secondary', mb: 2 }}>
              For your security, please verify <strong>{currentUser.email}</strong> before using the DTF Sheet Builder. If you don't see the email, check your junk/spam folder.
            </Typography>
            {verificationStatus ? (
              <Typography sx={{ mb: 2, color: '#1f2a12' }}>
                {verificationStatus}
              </Typography>
            ) : null}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>

              <Button
                variant="outlined"
                onClick={handleResendVerification}
                disabled={resendingVerification || cooldownMsLeft > 0}
                sx={{ border: '2px solid #94C940', color: '#4f5a38' }}
              >
                {resendingVerification
                  ? "Sending…"
                  : cooldownMsLeft > 0
                    ? `Wait ${Math.ceil(cooldownMsLeft / 1000)}s`
                    : "Resend email"}
              </Button>
              <Button
                variant="contained"
                onClick={handleCheckVerified}
                disabled={verificationLoading}
                sx={{ backgroundColor: '#94C940', '&:hover': { backgroundColor: '#86b532' } }}
              >
                I verified
              </Button>
            </Box>
            <Typography sx={{ mt: 3, color: 'text.secondary', fontSize: 13, textAlign: 'center' }}>
              If you experience issues, please contact{" "}
              <a href="mailto:cs@seeourdesigns.com" style={{ color: "#94C940", fontWeight: 600 }}>
                cs@seeourdesigns.com
              </a>.
            </Typography>
          </Paper>
        </Box>
      )}
      {isMobile ? (
        <div
          {...dropzoneProps}
          style={{
            padding: 20, maxWidth: "65rem",
            margin: '0 auto',
            alignItems: 'center',
            minHeight: "100vh",
            overflowY: "auto",
            justifyContent: 'center',
            position: 'relative',
          }}>
          {isDragging && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 9999,
              border: '2px dashed #94C940',
              background: 'rgba(148, 201, 64, 0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none',
              borderRadius: 12,
            }}>
              <div style={{
                padding: '1rem 1.25rem',
                background: 'white',
                borderRadius: 8,
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                fontFamily: 'Anek Latin',
                fontWeight: 800,
                color: '#5A6872'
              }}>
                Drop images to upload
              </div>
            </div>
          )}
          {images.length === 0 ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '2rem', flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                <h1>FILE MANAGER</h1>
                <UploadButton
                  onUpload={(newImages) => {
                    newImages.forEach(img => {
                      console.log('Uploaded image URL:', img.url || img.previewUrl || img.src);
                    });
                    setImages(prev => {
                      const updated = [...prev, ...newImages];
                      const serializableImages = updated.map(img => ({
                        name: img.name || img.fileName || img.file?.name,
                        widthIn: img.widthIn,
                        heightIn: img.heightIn,
                        quantity: img.quantity || 1,
                        src: img.url || img.previewUrl || img.src,
                        lockAspectRatio: true,
                        fromRecent: img.fromRecent === true,
                      }));
                      saveSessionData(sessionId, { images: serializableImages });
                      return updated;
                    });
                  }}
                />
                <Button variant="contained" startIcon={<ClockIcon />} sx={{
                  mb: 1,
                  backgroundColor: '#94C940',
                  color: 'white',
                  borderRadius: '8px',
                  px: 3,
                  py: 0,
                  fontWeight: '1000',
                  fontFamily: 'Anek Latin',
                  fontSize: '1.4rem',
                  textTransform: 'uppercase',
                  '&:hover': {
                    backgroundColor: '#94C940',
                  }
                }} onClick={() => setShowRecentDialog(true)} style={{ marginTop: '1rem' }}>
                  Recent
                </Button>
              </div>
              <div style={{
                width: '300px',
                backgroundColor: '#eaf3d4',
                padding: '1.5rem',
                fontFamily: 'Anek Latin',
                fontSize: '0.9rem',
                color: '#333',
                lineHeight: '1.5'
              }}>
                <h3 style={{ fontWeight: 'bold' }}>ART SPECIFICATIONS</h3>
                <p style={{ textAlign: 'left' }}>In order to ensure the highest quality print for transfers, please be sure to upload files meeting these requirements:</p>
                <ul style={{ textAlign: 'left', listStyleType: 'square', paddingLeft: '1.5rem' }}>
                  <li>
                    Acceptable File Types:
                    <ul style={{ listStyleType: 'square', paddingLeft: '1.5rem' }}>
                      <li>
                        <strong style={{ color: '#5A6872' }}>.png files</strong> with a <strong style={{ color: '#5A6872' }}>transparent background</strong>
                      </li>
                    </ul>
                  </li>
                  <li>
                    Recommended Image Resolution: <br />
                    <ul style={{ listStyleType: 'square', paddingLeft: '1.5rem' }}>
                      <li>
                        <strong style={{ color: '#5A6872' }}>300 dpi</strong>. Any lower can result in a pixelated/fuzzy print quality.
                      </li>
                      <li>Note: most web files are a standard 72 dpi, which is far too low for print.</li>
                    </ul>
                  </li>
                </ul>
                <p style={{ fontSize: '0.9rem', color: '#888' }}>
                  Have a .jpg file? Need a background removed?<br />
                  Visit our <a href="/art-services" style={{ color: '#5A6872' }}>Art Services</a> page.
                </p>
                <h4 style={{ marginTop: '1.5rem' }}>SHEET BUILDER TUTORIAL</h4>
                <div style={{ width: '100%', paddingTop: '56.25%', position: 'relative', border: '2px solid #333' }}>
                  <VideoWithPlayOverlay />
                </div>
              </div>
            </div>
          ) : (
            <div id="ImageSheetSection">
              <ImageArranger
                ref={arrangerRef}
                images={images}
                setImages={setImages}
                onProceed={handleProceedToCheckout}
                onPreviewGenerated={({ previewImages, sheetDataUrls, sheetGroups, totalImages, totalCanvasArea, costPerInch, totalCost, pictures, previewMode }) => {
                  console.log('onPreviewGenerated received pictures:', pictures);
                  setPreviewImages(previewImages);
                  setSheetDataUrls([]);
                  setSheetGroups([]);
                  setTotalImages(totalImages);
                  setSquareInches(totalCanvasArea);
                  setTotalCost(totalCost);
                  setPictures(pictures);
                  setPreviewMode(previewMode || 'full');
                }}
              />
              <ProceedToCheckout
                ref={proceedRef}
                images={images}
                previewImages={previewImages}
                sheetDataUrls={sheetDataUrls}
                sheetGroups={sheetGroups}
                totalCanvasArea={squareInches}
                totalCost={totalCost}
                totalImages={totalImages}
                pictures={pictures}
                previewMode={previewMode}
                forcePicturesOnly
                renderButton={false}
              />
            </div>
          )}
          <RecentlyUploaded
            open={showRecentDialog}
            onClose={() => setShowRecentDialog(false)}
            onSelect={(imgData) => setImages(prev => [...prev, imgData])}
          />
        </div>
      ) : (
        <div
          {...dropzoneProps}
          style={{
            padding: 20, maxWidth: "65rem",
            margin: '0 auto',
            alignItems: 'center',
            minHeight: "100vh",
            overflowY: "auto",
            justifyContent: 'center',
            position: 'relative',
          }}>
          {isDragging && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 9999,
              border: '2px dashed #94C940',
              background: 'rgba(148, 201, 64, 0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none',
              borderRadius: 12,
            }}>
              <div style={{
                padding: '1rem 1.25rem',
                background: 'white',
                borderRadius: 8,
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                fontFamily: 'Anek Latin',
                fontWeight: 800,
                color: '#5A6872'
              }}>
                Drop images to upload
              </div>
            </div>
          )}
          {images.length === 0 ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '2rem', flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                <h1>FILE MANAGER</h1>
                <UploadButton
                  onUpload={(newImages) => {
                    newImages.forEach(img => {
                      console.log('Uploaded image URL:', img.url || img.previewUrl || img.src);
                    });
                    setImages(prev => {
                      const updated = [...prev, ...newImages];
                      const serializableImages = updated.map(img => ({
                        name: img.name || img.fileName || img.file?.name,
                        widthIn: img.widthIn,
                        heightIn: img.heightIn,
                        quantity: img.quantity || 1,
                        src: img.url || img.previewUrl || img.src,
                        lockAspectRatio: true
                      }));
                      saveSessionData(sessionId, { images: serializableImages });
                      return updated;
                    });
                  }}
                />
                <Button variant="contained" startIcon={<ClockIcon />} sx={{
                  mb: 1,
                  backgroundColor: '#94C940',
                  color: 'white',
                  borderRadius: '8px',
                  px: 3,
                  py: 0,
                  fontWeight: '1000',
                  fontFamily: 'Anek Latin',
                  fontSize: '1.4rem',
                  textTransform: 'uppercase',
                  '&:hover': {
                    backgroundColor: '#94C940',
                  }
                }} onClick={() => setShowRecentDialog(true)} style={{ marginTop: '1rem' }}>
                  Recent
                </Button>
              </div>
              <div style={{
                width: '300px',
                backgroundColor: '#eaf3d4',
                padding: '1.5rem',
                fontFamily: 'Anek Latin',
                fontSize: '0.9rem',
                color: '#333',
                lineHeight: '1.5'
              }}>
                <h3 style={{ fontWeight: 'bold' }}>ART SPECIFICATIONS</h3>
                <p style={{ textAlign: 'left' }}>In order to ensure the highest quality print for transfers, please be sure to upload files meeting these requirements:</p>
                <ul style={{ textAlign: 'left', listStyleType: 'square', paddingLeft: '1.5rem' }}>
                  <li>
                    Acceptable File Types:
                    <ul style={{ listStyleType: 'square', paddingLeft: '1.5rem' }}>
                      <li>
                        <strong style={{ color: '#5A6872' }}>.png files</strong> with a <strong style={{ color: '#5A6872' }}>transparent background</strong>
                      </li>
                    </ul>
                  </li>
                  <li>
                    Recommended Image Resolution: <br />
                    <ul style={{ listStyleType: 'square', paddingLeft: '1.5rem' }}>
                      <li>
                        <strong style={{ color: '#5A6872' }}>300 dpi</strong>. Any lower can result in a pixelated/fuzzy print quality.
                      </li>
                      <li>Note: most web files are a standard 72 dpi, which is far too low for print.</li>
                    </ul>
                  </li>
                </ul>
                <p style={{ fontSize: '0.9rem', color: '#888' }}>
                  Have a .jpg file? Need a background removed?<br />
                  Visit our <a href="/art-services" style={{ color: '#5A6872' }}>Art Services</a> page.
                </p>
                <h4 style={{ marginTop: '1.5rem' }}>SHEET BUILDER TUTORIAL</h4>
                <div style={{ width: '100%', paddingTop: '56.25%', position: 'relative', border: '2px solid #333' }}>
                  <VideoWithPlayOverlay />
                </div>
              </div>
            </div>
          ) : (
            <div id="ImageSheetSection">
              <ImageArranger
                ref={arrangerRef}
                images={images}
                setImages={setImages}
                onProceed={handleProceedToCheckout}
                onPreviewGenerated={({ previewImages, sheetDataUrls, sheetGroups, totalImages, totalCanvasArea, costPerInch, totalCost, pictures, previewMode }) => {
                  console.log('onPreviewGenerated received pictures:', pictures);
                  setPreviewImages(previewImages);
                  setSheetDataUrls([]);
                  setSheetGroups([]);
                  setTotalImages(totalImages);
                  setSquareInches(totalCanvasArea);
                  setTotalCost(totalCost);
                  setPictures(pictures);
                  setPreviewMode(previewMode || 'full');
                }}
              />
              <ProceedToCheckout
                ref={proceedRef}
                images={images}
                previewImages={previewImages}
                sheetDataUrls={sheetDataUrls}
                sheetGroups={sheetGroups}
                totalCanvasArea={squareInches}
                totalCost={totalCost}
                totalImages={totalImages}
                pictures={pictures}
                previewMode={previewMode}
                forcePicturesOnly
                renderButton={false}
              />
            </div>
          )}
          <RecentlyUploaded
            open={showRecentDialog}
            onClose={() => setShowRecentDialog(false)}
            onSelect={(imgData) => setImages(prev => [...prev, imgData])}
          />
        </div>
      )}
    </>
  );
}

export default App;
